<script lang="ts">
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import Star from '$lib/components/Star.svelte';

  // Each card carries a distinct visual artifact in its demo slot, so the
  // grid doesn't read as six copies of the same card. Layouts and content
  // types vary intentionally — chat, config block, rule block, endpoint
  // list, metrics strip, deploy log.
  type Category = {
    code: string;
    title: string;
    tagline: string;
    items: string[];
    href: string;
    hrefLabel: string;
    demo: 'hub' | 'builder' | 'crf' | 'connectors' | 'admin' | 'deploy';
  };

  const categories: Category[] = [
    {
      code: '01',
      title: 'User Surfaces',
      tagline: 'Where your people meet AI.',
      items: [
        'The Hub — per-user assistant with file system + code execution',
        'Embeddable widgets for customer-facing surfaces',
        'Teams, Slack, and API access for custom entry points',
      ],
      href: '/product/hub',
      hrefLabel: 'Hub & surfaces',
      demo: 'hub',
    },
    {
      code: '02',
      title: 'Agent Builder',
      tagline: 'Configure, test, ship — with IT in the loop.',
      items: [
        'Visual configuration — scope, permissions, behavior',
        'Sandbox testing with eval suites',
        'Publish flow with admin approval gates',
      ],
      href: '/product/builder',
      hrefLabel: 'Agent Builder',
      demo: 'builder',
    },
    {
      code: '03',
      title: 'Security & Governance',
      tagline: 'Control what AI does, not just what it sees.',
      items: [
        'Cumulative Restrictions Framework',
        'Per-agent, per-user, per-source permission scoping',
        'Immutable audit trail for every action',
      ],
      href: '/product/security',
      hrefLabel: 'Security model',
      demo: 'crf',
    },
    {
      code: '04',
      title: 'Connectors & Integrations',
      tagline: 'Wire AI into the systems where your business runs.',
      items: [
        'Azure AI Search, Cognitive Services, OpenAI deployments',
        'MCP and OpenAPI extension points',
        'Private-network access — no public egress',
      ],
      href: '/product/connectors',
      hrefLabel: 'Connectors',
      demo: 'connectors',
    },
    {
      code: '05',
      title: 'Admin & Observability',
      tagline: 'See everything. Govern everything.',
      items: [
        'Real-time monitoring — behavior, performance, cost',
        'Role-based access via Entra ID',
        'Central catalog — version and manage your fleet',
      ],
      href: '/product/admin',
      hrefLabel: 'Admin console',
      demo: 'admin',
    },
    {
      code: '06',
      title: 'Deployment & Infrastructure',
      tagline: 'One-click into your cloud.',
      items: [
        'Deploy from Azure Marketplace into your tenant',
        'Runs on your infrastructure — managed by us',
        'AWS and GCP coming 2026',
      ],
      href: '/pricing',
      hrefLabel: 'Ways to deploy',
      demo: 'deploy',
    },
  ];
</script>

<section id="what-you-get" class="bg-[#f9f9f4] relative">
  <GridLines variant="light" />

  <GridContent>
    <GridInner>
      <div class="py-[80px] lg:py-[110px]">
        <!-- Header -->
        <div class="flex flex-col items-start lg:items-center text-left lg:text-center mb-14 lg:mb-16 max-w-[820px] mx-auto">
          <p class="font-mono text-[12px] uppercase tracking-[0.12em] text-[#ee6018] mb-5">
            06 · What You Get
          </p>
          <h2 class="font-display font-[300] text-[40px] md:text-[52px] lg:text-[60px] leading-[1.02] tracking-[-0.035em] text-[#020202] mb-6">
            Everything you need to make it yours.
          </h2>
          <p class="font-body font-light text-[17px] lg:text-[19px] leading-[1.55] text-[#5C5855] max-w-[640px]">
            Six capability layers. One foundation. Every one is a chapter on its own page — here&rsquo;s the shape of the whole thing.
          </p>
        </div>

        <!-- 3x2 category grid — cards span grid-line to grid-line with gaps between. -->
        <div class="relative lg:-mx-[54px]">
          <div class="hidden lg:block absolute top-0 -left-[9.03vw] -right-[9.03vw] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[9px] top-[-9px]" />
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[9px] top-[-9px]" />

          <div class="hidden lg:block absolute bottom-0 -left-[9.03vw] -right-[9.03vw] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[9px] bottom-[-9px]" />
          <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[9px] bottom-[-9px]" />

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {#each categories as cat, i}
              <a
                href={cat.href}
                class="category-card bg-white border border-[#D3D3D3] p-7 lg:p-8 group relative flex flex-col"
              >
                <Star variant="bottom-right" color="#D3D3D3" class="hidden lg:block absolute -left-px -top-px" />
                <Star variant="bottom-left" color="#D3D3D3" class="hidden lg:block absolute -right-px -top-px" />
                <Star variant="top-right" color="#D3D3D3" class="hidden lg:block absolute -left-px -bottom-px" />
                <Star variant="top-left" color="#D3D3D3" class="hidden lg:block absolute -right-px -bottom-px" />

                <!-- Header -->
                <div class="flex items-baseline gap-3 mb-3">
                  <span class="font-mono text-[11px] text-[#ee6018]">{cat.code}</span>
                  <h3 class="font-display font-[350] text-[20px] lg:text-[22px] tracking-[-0.02em] text-[#020202]">
                    {cat.title}
                  </h3>
                </div>
                <p class="font-body font-light text-[14px] text-[#5C5855] mb-5">
                  {cat.tagline}
                </p>

                <!-- Bullets -->
                <ul class="flex flex-col gap-[10px] mb-6">
                  {#each cat.items as item}
                    <li class="flex items-start gap-3 font-body text-[13.5px] leading-[1.5] text-[#282828]">
                      <span class="mt-[9px] w-[10px] h-px bg-[#ee6018] shrink-0"></span>
                      <span>{item}</span>
                    </li>
                  {/each}
                </ul>

                <!-- Per-card visual artifact (varies by category) -->
                <div class="mt-auto mb-5 bg-[#fafaf6] border border-[#eae7df] px-4 py-3 font-mono text-[11px] leading-[1.7] text-[#3a3a3a] overflow-hidden">
                  {#if cat.demo === 'hub'}
                    <p class="text-[#5C5855]">you <span class="text-[#020202]">&nbsp;→&nbsp;</span>Summarize Q4 pipeline</p>
                    <p class="text-[#5C5855]">hub <span class="text-[#ee6018]">&nbsp;→&nbsp;</span>14 opps · $2.3M weighted · 3 slipping</p>
                  {:else if cat.demo === 'builder'}
                    <p><span class="text-[#ee6018]">agent</span> <span class="text-[#020202]">"Support"</span> {'{'}</p>
                    <p class="pl-3">scope  = [<span class="text-[#0b8a3e]">"read:tickets"</span>]</p>
                    <p class="pl-3">handoff = <span class="text-[#0b8a3e]">"human if refund &gt; 100"</span></p>
                    <p>{'}'}</p>
                  {:else if cat.demo === 'crf'}
                    <p class="text-[#5C5855]">untrusted: <span class="text-[#020202]">inbox/*</span></p>
                    <p class="text-[#5C5855]">on_taint : <span class="text-[#ee6018]">gate("human_review")</span></p>
                    <p class="text-[#5C5855]">log      : <span class="text-[#0b8a3e]">immutable</span></p>
                  {:else if cat.demo === 'connectors'}
                    <p><span class="text-[#ee6018]">→</span> azure.search <span class="text-[#5C5855]">· indexed, scoped</span></p>
                    <p><span class="text-[#ee6018]">→</span> mcp://crm <span class="text-[#5C5855]">· private net</span></p>
                    <p><span class="text-[#ee6018]">→</span> openai <span class="text-[#5C5855]">· your deployment</span></p>
                  {:else if cat.demo === 'admin'}
                    <div class="grid grid-cols-2 gap-y-1 gap-x-5">
                      <p><span class="text-[#5C5855]">agents</span> <span class="text-[#020202]">14</span></p>
                      <p><span class="text-[#5C5855]">events/m</span> <span class="text-[#020202]">2.1k</span></p>
                      <p><span class="text-[#5C5855]">cost/day</span> <span class="text-[#020202]">$42</span></p>
                      <p><span class="text-[#5C5855]">alerts</span> <span class="text-[#0b8a3e]">0</span></p>
                    </div>
                  {:else if cat.demo === 'deploy'}
                    <p><span class="text-[#5C5855]">$</span> az marketplace corral deploy --tenant=acme</p>
                    <p class="text-[#0b8a3e]">✓ deployed in 28m · running in your tenant</p>
                  {/if}
                </div>

                <!-- Link -->
                <span class="font-mono text-[12px] text-[#282828] inline-flex items-center gap-1.5 group-hover:text-[#ee6018] transition-colors">
                  {cat.hrefLabel}
                  <span aria-hidden="true" class="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </a>
            {/each}
          </div>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<style>
  .category-card {
    transition: background-color 160ms ease-out;
  }
  .category-card:hover {
    background-color: #fdfdfb;
  }
</style>
